import { useTranslations } from '@vocab/react';
import {
  Badge,
  Box,
  Checkbox,
  Heading,
  Inline,
  Stack,
  Text,
} from 'braid-design-system';
import { Fragment, type FormEvent } from 'react';

import translations from './.vocab';
import { AiLogoSvg } from './AiLogoSvg';

import * as styles from './index.css';

export const enum SuggestionOperation {
  ADD = 'ADD',
  REPLACE = 'REPLACE',
}

export type Suggestion = {
  id: string;
  title: string;
  topic: string;
  operation: SuggestionOperation;
  reason: string;
  example: string;
};

export interface OptimisationSuggestionsProps {
  suggestions: Suggestion[];
  onSuggestionSelected: (
    e: FormEvent<HTMLInputElement>,
    suggestionId: string,
  ) => void;
  selectedSuggestions: string[];
}

export const isSuggestionSelected = (id: string, suggestions: string[]) =>
  Boolean(suggestions.find((suggestionId) => suggestionId === id));

export const OptimisationSuggestions = ({
  suggestions,
  onSuggestionSelected,
  selectedSuggestions,
}: OptimisationSuggestionsProps) => {
  const { t } = useTranslations(translations);

  return (
    <>
      <Stack space="medium">
        <AiLogoSvg />
        <Inline space="xsmall" alignY="center">
          <Heading level="4">{t('header-text')}</Heading>
          <Badge tone="neutral">{suggestions.length}</Badge>
        </Inline>
        <Text size="small">{t('description-text')}</Text>
        <Box className={styles.scrollable} paddingRight="medium">
          <Stack space="medium">
            {suggestions.map((suggestion: Suggestion) => (
              <Fragment key={suggestion.id}>
                <Checkbox
                  id={suggestion.id}
                  checked={isSuggestionSelected(
                    suggestion.id,
                    selectedSuggestions,
                  )}
                  onChange={(event) => {
                    onSuggestionSelected(event, suggestion.id);
                  }}
                  size="small"
                  label={suggestion.title}
                  description={suggestion.reason}
                  aria-describedby={`${suggestion.id}_example`}
                />
                <Box
                  background="neutralSoft"
                  padding="medium"
                  borderRadius="standard"
                  marginLeft="medium"
                >
                  <Text
                    size="small"
                    tone="secondary"
                    id={`${suggestion.id}_example`}
                  >
                    {suggestion.example}
                  </Text>
                </Box>
              </Fragment>
            ))}
          </Stack>
        </Box>
      </Stack>
    </>
  );
};
